// Copyright © 2025 The Things Industries B.V.

import tts from '@console/api/tts'

import createRequestLogic from '@ttn-lw/lib/store/logics/create-request-logic'
import attachPromise from '@ttn-lw/lib/store/actions/attach-promise'

import * as labels from '@console/store/actions/labels.tti'
import { getApplicationsList } from '@console/store/actions/applications'
import { getGatewaysList } from '@console/store/actions/gateways'
import { getDevicesList } from '@console/store/actions/devices'

const createLabelLogic = createRequestLogic({
  type: labels.CREATE_LABEL,
  process: async ({ action }) => {
    const { label } = action.payload

    return await tts.Labels.createLabel(label)
  },
})

const getLabelLogic = createRequestLogic({
  type: labels.GET_LABEL,
  process: async ({ action }) => {
    const {
      payload: { id },
      meta: { selector },
    } = action
    const label = await tts.Labels.getLabelById(id, selector)

    return label
  },
})

const updateLabelLogic = createRequestLogic({
  type: labels.UPDATE_LABEL,
  process: async ({ action }) => {
    const {
      payload: { id, patch },
    } = action
    const result = await tts.Labels.updateLabelById(id, patch)

    return { ...patch, ...result }
  },
})

const deleteLabelLogic = createRequestLogic({
  type: labels.DELETE_LABEL,
  process: async ({ action }) => {
    const { id } = action.payload

    await tts.Labels.deleteLabelById(id)

    return { id }
  },
})

const fetchLabelsThroughPagination = async selectors => {
  let page = 1
  const limit = 100
  let totalCount = Infinity
  let data = {}

  while ((page - 1) * limit < totalCount) {
    // eslint-disable-next-line no-await-in-loop
    const result = await tts.Labels.getAllLabels({ page, limit, order: '-created_at' }, selectors)

    totalCount = result.totalCount
    page += 1
    data = { ...data, ...result }
  }

  return data
}

const getLabelsLogic = createRequestLogic({
  type: labels.GET_LABEL_LIST,
  process: async ({ action }) => {
    const {
      params: { page, limit, query, order },
    } = action.payload

    const { selectors, options } = action.meta

    const data = options.isSearch
      ? await tts.Labels.search(
          {
            page,
            limit,
            name_contains: query,
            order,
          },
          selectors,
        )
      : options.all
        ? await fetchLabelsThroughPagination(selectors)
        : await tts.Labels.getAllLabels({ page, limit, order }, selectors)

    return { entities: data.labels, totalCount: data.totalCount }
  },
})

const getLabelEntitiesLogic = createRequestLogic({
  type: labels.GET_LABEL_ENTITIES,
  process: async ({ action }, dispatch) => {
    const {
      payload: { label, options },
    } = action
    const { isSearch, query, tab } = options
    const entities = []
    let totalCount = 0
    if ('applications' in label?.entities && (tab === 'applications' || tab === 'all')) {
      const result = await dispatch(
        attachPromise(
          getApplicationsList(
            {
              query: isSearch ? query : undefined,
              label_id_contains: label.ids.label_id,
            },
            ['name', 'created_at'],
            {
              isSearch: true,
            },
          ),
        ),
      )
      entities.push(...result.entities)
      totalCount += result.totalCount
    }
    if ('gateways' in label?.entities && (tab === 'gateways' || tab === 'all')) {
      const result = await dispatch(
        attachPromise(
          getGatewaysList(
            {
              query: isSearch ? query : undefined,
              label_id_contains: label.ids.label_id,
            },
            ['name', 'created_at'],
            {
              isSearch: true,
            },
          ),
        ),
      )
      entities.push(...result.entities)
      totalCount += result.totalCount
    }
    if ('end_devices' in label?.entities && (tab === 'devices' || tab === 'all')) {
      const deviceFetch = []
      let appId = ''
      label.entities.end_devices.forEach(device => {
        const currentAppId = device.application_ids.application_id
        if (currentAppId !== appId) {
          appId = currentAppId
          deviceFetch.push(
            dispatch(
              attachPromise(
                getDevicesList(
                  device.application_ids.application_id,
                  {
                    query: isSearch ? query : undefined,
                    label_id_contains: label.ids.label_id,
                  },
                  ['name', 'created_at'],
                ),
              ),
            ),
          )
        }
      })
      const result = await Promise.all(deviceFetch)
      entities.push(...result.map(({ entities }) => entities).flat())
      totalCount += result.reduce((acc, { totalCount }) => acc + totalCount, 0)
    }

    return { entities, totalCount }
  },
})

export default [
  createLabelLogic,
  getLabelLogic,
  updateLabelLogic,
  deleteLabelLogic,
  getLabelsLogic,
  getLabelEntitiesLogic,
]
